import { useMemo } from 'react';
import {
  type InputFormData,
  type InputDisabledFields,
} from '@ikea-customer-self-serve/contact-form';
import {
  getConditionalFormValues,
  type ConditionalFormValues,
} from '../helpers/formHelper';
import { useStateSelector } from '../store/';
import { type UserState } from '../reducers/user/userTypes';

interface InitStateArg {
  user: UserState;
  conditionalFormValues: ConditionalFormValues;
}

interface UseContactFormState {
  formState: InputFormData;
  disabledFields: InputDisabledFields;
}

const initState = ({
  user,
  conditionalFormValues,
}: InitStateArg): InputFormData => {
  const privacyPolicy = user.privacyPolicy;
  const updateProfile = conditionalFormValues.updateProfile;

  return {
    address1: conditionalFormValues.address1,
    address2: conditionalFormValues.address2,
    allowPersonalEmail: user.allowPersonalEmail,
    allowSalesProfileSharing: undefined,
    businessName: user.businessName,
    city: conditionalFormValues.city,
    customerNumber: conditionalFormValues.customerNumber,
    email: user.email,
    foreignCountry: undefined,
    firstName: user.firstName,
    firstNamePhonetic: user.firstNamePhonetic,
    lastNamePhonetic: user.lastNamePhonetic,
    mobileNumber: user.phoneNumber,
    postCode: conditionalFormValues.postCode,
    privacyPolicy,
    recipientCode: undefined,
    recipientCodeType: undefined,
    state: conditionalFormValues.state,
    surName: user.lastName,
    taxNo: undefined,
    taxCodeType: undefined,
    updateProfile,
  };
};

function useContactFormFields(
  operation: string,
  showMinimalForm: boolean,
): [UseContactFormState] {
  const user = useStateSelector((state) => state.user);
  const search = useStateSelector((state) => state.search);
  const disabledPrevButton = useStateSelector(
    (state) => state.app.disabledPrevButton,
  );

  const conditionalFormValues = useMemo(
    () =>
      getConditionalFormValues(
        user,
        search,
        operation,
        disabledPrevButton,
        showMinimalForm,
      ),
    [user, search, operation, disabledPrevButton, showMinimalForm],
  );

  const formState = useMemo<InputFormData>(
    () => initState({ user, conditionalFormValues }),
    [user, conditionalFormValues],
  );

  const disabledFields = useMemo<InputDisabledFields>(
    () => ({
      city: !!conditionalFormValues.city,
      postCode: !!conditionalFormValues.postCode,
      state: !!conditionalFormValues.state,
      email: !!user.email,
      address1: !!search.address,
    }),
    [conditionalFormValues, user.email, search.address],
  );

  return [{ formState, disabledFields }];
}

export default useContactFormFields;
